import { useState, useEffect } from "react";
import { adminAPI } from "../../api";
import { formatFecha } from "../../utils";
import { useAuth } from "../../context/AuthContext";
import { useNavigate } from "react-router-dom";
import CampoPassword from "../../components/admin/CampoPassword";
import { passwordValida, MENSAJE_REGLAS } from "../../constants/password";
import toast from "react-hot-toast";
import "./Usuarios.css";

const FORM_VACIO = { name: "", username: "", password: "", role: "usuario" };

export default function Usuarios() {
  const { esSuperAdmin } = useAuth();
  const navigate = useNavigate();
  const [usuarios, setUsuarios] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [modalAbierto, setModalAbierto] = useState(false);
  const [modalPassword, setModalPassword] = useState(null); // { id, name }
  const [form, setForm] = useState(FORM_VACIO);
  const [nuevaPassword, setNuevaPassword] = useState("");
  const [guardando, setGuardando] = useState(false);
  const [confirmEliminar, setConfirmEliminar] = useState(null);

  // Redirigir si no es super_admin
  useEffect(() => {
    if (!esSuperAdmin) navigate("/admin/dashboard");
  }, [esSuperAdmin, navigate]);

  const cargar = async () => {
    setCargando(true);
    try {
      const res = await adminAPI.listarUsuarios();
      setUsuarios(res.data.data);
    } catch { toast.error("Error cargando usuarios"); }
    finally { setCargando(false); }
  };

  useEffect(() => { cargar(); }, []);

  const handleCrear = async (e) => {
    e.preventDefault();
    if (!form.name || !form.username || !form.password) {
      toast.error("Nombre, usuario y contraseña son requeridos");
      return;
    }
    if (!passwordValida(form.password)) {
      toast.error(MENSAJE_REGLAS);
      return;
    }
    setGuardando(true);
    try {
      await adminAPI.crearUsuario(form);
      toast.success("Usuario creado correctamente");
      setModalAbierto(false);
      setForm(FORM_VACIO);
      cargar();
    } catch (err) {
      toast.error(err.response?.data?.message || "Error creando usuario");
    } finally {
      setGuardando(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (!passwordValida(nuevaPassword)) {
      toast.error(MENSAJE_REGLAS);
      return;
    }
    setGuardando(true);
    try {
      await adminAPI.resetearPassword(modalPassword.id, nuevaPassword);
      toast.success(`Contraseña de ${modalPassword.name} actualizada`);
      setModalPassword(null);
      setNuevaPassword("");
    } catch (err) {
      toast.error(err.response?.data?.message || "Error actualizando contraseña");
    } finally {
      setGuardando(false);
    }
  };

  const handleEliminar = async (id) => {
    try {
      await adminAPI.eliminarUsuario(id);
      toast.success("Usuario eliminado");
      setConfirmEliminar(null);
      cargar();
    } catch (err) {
      toast.error(err.response?.data?.message || "Error eliminando usuario");
    }
  };

  return (
      <div className="usuarios-page">

        {/* Modal crear usuario */}
        {modalAbierto && (
            <div className="modal-overlay" onClick={() => setModalAbierto(false)}>
              <div className="modal-box card" onClick={(e) => e.stopPropagation()}>
                <div className="modal-header">
                  <h3>➕ Nuevo usuario</h3>
                  <button className="btn btn-ghost btn-sm" onClick={() => setModalAbierto(false)}>✕</button>
                </div>
                <form onSubmit={handleCrear} className="modal-form">
                  <div className="form-group">
                    <label className="form-label">Nombre completo *</label>
                    <input className="form-input" value={form.name}
                           onChange={(e) => setForm({ ...form, name: e.target.value })}
                           placeholder="Ej: Carlos García" autoFocus />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Nombre de usuario *</label>
                    <input className="form-input" value={form.username}
                           onChange={(e) => setForm({ ...form, username: e.target.value.toLowerCase().replace(/\s+/g, "_") })}
                           placeholder="Ej: carlos o carlos_garcia" autoCapitalize="none" />
                    <span className="form-hint">Solo letras minúsculas, números y guión bajo. Sin espacios.</span>
                  </div>
                  <div className="form-group">
                    <label className="form-label" htmlFor="nuevo-usuario-password">Contraseña *</label>
                    <CampoPassword
                        id="nuevo-usuario-password"
                        value={form.password}
                        onChange={(e) => setForm({ ...form, password: e.target.value })}
                        placeholder="Ej. Casa2024"
                        mostrarReglas
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Rol</label>
                    <select className="form-select" value={form.role}
                            onChange={(e) => setForm({ ...form, role: e.target.value })}>
                      <option value="usuario">Usuario</option>
                      <option value="super_admin">Super Admin</option>
                    </select>
                    <span className="form-hint">
                  {form.role === "super_admin"
                      ? "⚠️ El Super Admin puede gestionar todos los usuarios y propiedades."
                      : "El usuario solo puede gestionar sus propias propiedades."}
                </span>
                  </div>
                  <div className="modal-footer">
                    <button type="button" className="btn btn-ghost" onClick={() => setModalAbierto(false)}>Cancelar</button>
                    <button type="submit" className="btn btn-accent" disabled={guardando}>
                      {guardando ? "Creando..." : "✅ Crear usuario"}
                    </button>
                  </div>
                </form>
              </div>
            </div>
        )}

        {/* Modal resetear contraseña */}
        {modalPassword && (
            <div className="modal-overlay" onClick={() => setModalPassword(null)}>
              <div className="modal-box card" onClick={(e) => e.stopPropagation()}>
                <div className="modal-header">
                  <h3>🔑 Cambiar contraseña</h3>
                  <button className="btn btn-ghost btn-sm" onClick={() => setModalPassword(null)}>✕</button>
                </div>
                <form onSubmit={handleResetPassword} className="modal-form">
                  <p style={{ color: "var(--color-text-muted)", fontSize: ".9rem" }}>
                    Cambiando contraseña de <strong>{modalPassword.name}</strong> (@{modalPassword.username})
                  </p>
                  <div className="form-group">
                    <label className="form-label" htmlFor="reset-password">Nueva contraseña *</label>
                    <CampoPassword
                        id="reset-password"
                        value={nuevaPassword}
                        onChange={(e) => setNuevaPassword(e.target.value)}
                        placeholder="Ej. Casa2024"
                        autoFocus
                        mostrarReglas
                    />
                  </div>
                  <div className="modal-footer">
                    <button type="button" className="btn btn-ghost" onClick={() => setModalPassword(null)}>Cancelar</button>
                    <button type="submit" className="btn btn-primary" disabled={guardando}>
                      {guardando ? "Guardando..." : "Actualizar contraseña"}
                    </button>
                  </div>
                </form>
              </div>
            </div>
        )}

        {/* Modal confirmar eliminar */}
        {confirmEliminar && (
            <div className="modal-overlay" onClick={() => setConfirmEliminar(null)}>
              <div className="modal-box card" onClick={(e) => e.stopPropagation()}>
                <div className="modal-header">
                  <h3>¿Eliminar usuario?</h3>
                </div>
                <div className="modal-form">
                  <p style={{ color: "var(--color-text-muted)" }}>
                    Vas a eliminar a <strong>{confirmEliminar.name}</strong> (@{confirmEliminar.username}).
                    Sus propiedades quedarán sin asignar. Esta acción no se puede deshacer.
                  </p>
                  <div className="modal-footer">
                    <button className="btn btn-ghost" onClick={() => setConfirmEliminar(null)}>Cancelar</button>
                    <button className="btn btn-danger" onClick={() => handleEliminar(confirmEliminar.id)}>
                      Sí, eliminar
                    </button>
                  </div>
                </div>
              </div>
            </div>
        )}

        {/* Header */}
        <div className="page-header">
          <div>
            <h1 className="heading-serif page-title">Usuarios</h1>
            <p style={{ color: "var(--color-text-muted)", fontSize: ".9rem" }}>
              {usuarios.length} usuario{usuarios.length !== 1 ? "s" : ""} registrado{usuarios.length !== 1 ? "s" : ""}
            </p>
          </div>
          <button className="btn btn-accent" onClick={() => setModalAbierto(true)}>
            + Nuevo usuario
          </button>
        </div>

        {/* Tabla */}
        {cargando ? (
            <div className="spinner" />
        ) : (
            <div className="table-container card">
              <table className="data-table">
                <thead>
                <tr>
                  <th>Usuario</th>
                  <th>Username</th>
                  <th>Rol</th>
                  <th>Propiedades</th>
                  <th>Último acceso</th>
                  <th>Creado</th>
                  <th>Acciones</th>
                </tr>
                </thead>
                <tbody>
                {usuarios.map((u) => (
                    <tr key={u.id}>
                      <td>
                        <div style={{ display: "flex", alignItems: "center", gap: ".75rem" }}>
                          <div style={{
                            width: 36, height: 36, borderRadius: "50%",
                            background: u.role === "super_admin" ? "var(--color-accent)" : "var(--color-primary)",
                            color: "#fff", display: "flex", alignItems: "center", justifyContent: "center",
                            fontWeight: 700, fontSize: ".9rem", flexShrink: 0,
                          }}>
                            {u.name.charAt(0).toUpperCase()}
                          </div>
                          <span style={{ fontWeight: 600 }}>{u.name}</span>
                        </div>
                      </td>
                      <td style={{ fontFamily: "monospace", fontSize: ".875rem" }}>@{u.username}</td>
                      <td>
                    <span style={{
                      background: u.role === "super_admin" ? "rgba(201,169,110,.15)" : "var(--color-bg-secondary)",
                      color: u.role === "super_admin" ? "var(--color-accent)" : "var(--color-text-muted)",
                      padding: ".2rem .65rem", borderRadius: "999px",
                      fontSize: ".75rem", fontWeight: 700,
                    }}>
                      {u.role === "super_admin" ? "Super Admin" : "Usuario"}
                    </span>
                      </td>
                      <td style={{ textAlign: "center" }}>{u.total_propiedades || 0}</td>
                      <td style={{ fontSize: ".8rem", color: "var(--color-text-muted)" }}>
                        {u.last_login ? formatFecha(u.last_login) : "Nunca"}
                      </td>
                      <td style={{ fontSize: ".8rem", color: "var(--color-text-muted)" }}>
                        {formatFecha(u.created_at)}
                      </td>
                      <td>
                        <div className="action-btns">
                          <button
                              className="btn btn-ghost btn-sm"
                              title="Cambiar contraseña"
                              onClick={() => { setModalPassword(u); setNuevaPassword(""); }}
                          >🔑</button>
                          <button
                              className="btn btn-ghost btn-sm"
                              title="Eliminar usuario"
                              style={{ color: "var(--color-error)" }}
                              onClick={() => setConfirmEliminar(u)}
                          >🗑️</button>
                        </div>
                      </td>
                    </tr>
                ))}
                </tbody>
              </table>
            </div>
        )}
      </div>
  );
}